import { useParamCart } from './useParamCart'
import { useParamGlobal } from './useParamGlobal'
import {
  dataLayerSetupSchema,
  type GASetupDatalayer,
  type GASetupDatalayerParamItems,
  type GASetupDatalayerParamPage,
  type GASetupDatalayerParamSales,
  type GASetupDatalayerParamStore,
  type GASetupDatalayerParamUser,
  type GASetupDatalayerParamWishlist,
} from '@integration-layer/schemas/GAEvents/dataLayerSetup'
import { useResetParams } from './useResetParams'

/**
 * Composable for setting up the Google Analytics DataLayer.
 *
 * This function is called on app.vue and error.vue on page change to initialize
 * and configure the DataLayer with various parameters such as cart, user, items,
 * wishlist, page, and store information.
 *
 * This composable dispatches the ACTUAL GTM 'Armani_DL_Setup' EVENT, relying on other composables to map the parameters:
 * - `useParamCart` for cart-related parameters
 * - `useParamGlobal` for global parameters
 * - `useCustomer` for user info
 * - `useParamPage` for page-specific parameters
 *
 * All these and other parameters are passed from the specific composables to useDatalayerSetup using the useStates saved by the `useGAEntity` composables.
 * To track the data mapping, search for the `useGAEntity` id parameter in the codebase.
 *
 *
 * Key responsibilities:
 * - Initialize DataLayer with comprehensive event tracking parameters
 * - Set up entities for different tracking aspects
 * - Dispatch initial tracking events
 * - Handle cart abandonment tracking
 *
 * @async
 * @returns {Promise<void>}
 */
export const useDatalayerSetup = async () => {
  /**
   * Destructure reset parameter methods and default values.
   * @type {Object}
   */
  const {
    setParamItems,
    setParamUser,
    setParamWishlist,
    setParamPages,
    defaultValues,
  } = useResetParams()

  /**
   * Check user login status.
   * @type {Ref<boolean>}
   */
  const { isLogged } = useLogin()

  /**
   * Sales delivery type entity for tracking delivery information.
   * @type {Ref<string | undefined>}
   */
  const salesDeliveryTypeEntity = useGAEntity<
    GASetupDatalayerParamSales['delivery_type'] | undefined
  >('GA_DL_SETUP_SALES_DELIVERY_TYPE')

  /**
   * Sales payment type entity for tracking payment information.
   * @type {Ref<Object | undefined>}
   */
  const salesPaymentTypeEntity = useGAEntity<
    Omit<GASetupDatalayerParamSales, 'delivery_type'> | undefined
  >('GA_DL_SETUP_SALES_PAYMENT_TYPE')

  /**
   * Prepare sales parameters for tracking.
   * @type {GASetupDatalayerParamSales}
   */
  const paramSales: GASetupDatalayerParamSales = {
    delivery_type: salesDeliveryTypeEntity.value ?? '',
    payment_type: salesPaymentTypeEntity.value?.payment_type ?? '',
    checkout_type: isLogged.value ? 1 : 0,
  }

  /**
   * User entity for tracking user-related information.
   * @type {Ref<GASetupDatalayerParamUser>}
   */
  const userEntity = useGAEntity<GASetupDatalayerParamUser>('GA_DL_SETUP_USER')
  const paramUser = setParamUser(userEntity.value)

  /**
   * Items entity for tracking product-related information.
   * @type {Ref<GASetupDatalayerParamItems>}
   */
  const itemsEntity =
    useGAEntity<GASetupDatalayerParamItems>('GA_DL_SETUP_ITEMS')
  const paramItems = setParamItems(itemsEntity.value)

  /**
   * Wishlist entity for tracking wishlist-related information.
   * @type {Ref<GASetupDatalayerParamWishlist>}
   */
  const wishlistEntity = useGAEntity<GASetupDatalayerParamWishlist>(
    'GA_DL_SETUP_WISHLIST'
  )
  const paramWishlist = setParamWishlist(wishlistEntity.value)

  /**
   * Page entity for tracking page-related information.
   * @type {Ref<GASetupDatalayerParamPage | undefined>}
   */
  const pageEntity = useGAEntity<GASetupDatalayerParamPage | undefined>(
    'GA_DL_SETUP_PAGE'
  )
  // `page_slug` is a TS-level side channel on GASetupDatalayerParamPage,
  // populated by useParamPage for in-app consumers (e.g. carousel
  // select_item attribution on editorial pages). It is NOT part of the
  // GTM event schema, so strip it before assembling the dataLayer payload.
  const { page_slug: _page_slug, ...paramPage } = setParamPages(
    pageEntity.value
  )

  /**
   * Store parameters (currently a placeholder, to be implemented with store locator).
   * @type {GASetupDatalayerParamStore}
   */
  const paramStore: GASetupDatalayerParamStore = { city_store: '', store: '' }

  /**
   * Complete event payload for DataLayer setup.
   * @type {GASetupDatalayer}
   */
  const eventPayload: GASetupDatalayer = {
    event: 'Armani_DL_Setup',
    paramCart: useParamCart(),
    paramGlobal: useParamGlobal(),
    paramSales,
    paramUser,
    paramItems,
    paramWishlist,
    paramPage,
    paramStore,
  }

  /**
   * Track whether DataLayer has been initialized to prevent duplicate initialization.
   * @type {Ref<boolean>}
   */
  // THIS CHECK HAS BEEN REMOVED BECAUSE IT WAS PREVENTING THE EVENT TO BE SENT ON PAGE CHANGES
  //   const dataLayerInitilized = useState(`datalayer-initialized`, () => false)

  // Send initial DataLayer event if not already initialized
  //   if (!dataLayerInitilized.value) {
  useGASendEvent(dataLayerSetupSchema, eventPayload)
  //     dataLayerInitilized.value = true
  //   }

  const nuxt = useNuxtApp()

  // Sets the last page where the dlSetup was emitted:
  // other events waiting for DL setup check if it was already fired before their own hook listener was set up
  nuxt._dlSetupLastPath = nuxt.$router.currentRoute.value.path

  // Notify listeners (e.g. Contentsquare plugin) that the Armani_DL_Setup
  // event has been pushed. The payload is the same object pushed to
  // window.dataLayer so listeners don't need to scan the dataLayer.
  await nuxt.callHook('armani_dl_setup-event-fired', eventPayload)

  /**
   * Cart initialization and abandonment tracking.
   */
  const { cartInitialized, cart } = useCart()
  const { dispatchAbandonCartEvent } = useGACartEvents()

  // Watch cart initialization and dispatch abandonment event if applicable
  watch(
    cartInitialized,
    value => {
      if (value && cart.value?.line_items?.length) {
        dispatchAbandonCartEvent()
      }
    },
    { immediate: true }
  )

  // Reset items entity to default values
  itemsEntity.value = defaultValues.paramItems

  // Trigger hook to signal that page view processing is complete
  //@ts-ignore
  nuxt.callHook('app:after-page-view')
}
